import {useMemo} from 'react';
import {Popover, Box} from '@shopify/polaris';
import {useToggle, useMediaLayout} from '@shopify/react-hooks';
import {useGroupedTranslatableRoutes} from 'utilities/use-grouped-translatable-routes';
import {useI18n} from '@shopify/react-i18n';
import {
  SelectionList,
  PlainAction,
  BottomSheet,
} from '@shopify/online-store-ui';

export function ResourceTypeSelector({disabled}: {disabled: boolean}) {
  const [i18n] = useI18n();
  const mobile = useMediaLayout('(max-width: 48rem)');
  const {
    value: resourceTypeSelectorVisible,
    toggle: toggleResourceTypeSelector,
    setFalse: hideResourceTypeSelector,
  } = useToggle(false);
  const groupedTranslatableRoutes = useGroupedTranslatableRoutes();
  const currentResourceLabel = useMemo(
    () =>
      groupedTranslatableRoutes
        .flatMap((routeGroup) => routeGroup.items)
        .find((route) => route.current),
    [groupedTranslatableRoutes],
  );

  const sections = groupedTranslatableRoutes.map(({label, items}) => ({
    title: label,
    items: items.map((item) => ({
      content: item.label,
      onAction: () => {
        if (item.current) return;
        item.navigate(false);
        hideResourceTypeSelector();
      },
      active: item.current,
      disabled,
    })),
  }));

  const plainActivator = (
    <PlainAction
      onAction={toggleResourceTypeSelector}
      disclosure
      content={
        currentResourceLabel?.label ??
        i18n.translate('ResourceTypeSelector.selectType')
      }
      fontWeight="semibold"
      disabled={disabled}
      slim={mobile}
      truncate
    />
  );

  const popoverActivator = (
    <Popover
      active={resourceTypeSelectorVisible}
      activator={plainActivator}
      onClose={hideResourceTypeSelector}
      preferredAlignment="center"
    >
      <Box width="13rem" maxWidth="13rem">
        <SelectionList sections={sections} />
      </Box>
    </Popover>
  );

  const bottomSheetMarkup = mobile && (
    <BottomSheet
      expansion={
        !disabled && resourceTypeSelectorVisible ? 'full' : 'collapsed'
      }
      onExpansionChange={toggleResourceTypeSelector}
      titleTapExpansion="full"
      horizontalHeaderPadding="0"
      fullCollapse
      isModal
    >
      <SelectionList sections={sections} />
    </BottomSheet>
  );

  const resourceTypeSelectorMarkup = mobile ? plainActivator : popoverActivator;

  return (
    <>
      {resourceTypeSelectorMarkup}
      {bottomSheetMarkup}
    </>
  );
}
